import ServiceTemplate from '../../../../templates/service/ServiceTemplate';
import type { ServiceConfig } from '../../../../templates/service/types';

const config: ServiceConfig = {
  // SEO
  pageTitle: 'Electrical Installations Sleaford | Quantum Electrical Services',
  metaDescription: 'Professional electrical installations in Sleaford, Lincolnshire. New circuits, sockets, rewires & full electrical fit-outs by NAPIT registered electricians. Part P certified. Call 01529 265060.',
  keywords: 'electrical installations Sleaford, electrician Sleaford, new electrical installation Sleaford, domestic electrical installation Sleaford Lincolnshire, NAPIT electrician Sleaford, Part P certificate Sleaford, new circuit installation Sleaford, electrical fit out Sleaford',
  canonicalPath: '/services/installation-upgrades/electrical-installations',

  // Hero
  heroBadge: 'NAPIT Registered · Sleaford & Lincolnshire',
  heroTitle: 'Electrical Installations',
  heroHighlight: 'in Sleaford',
  heroLocation: 'Lincolnshire',
  heroDesc: 'New circuits, full electrical fit-outs, socket installations and everything in between — Quantum Electrical delivers first-class electrical installations across Sleaford and Lincolnshire. NAPIT registered, Part P certified, and trusted by hundreds of local homeowners and businesses.',
  heroImageUrl: 'https://public.readdy.ai/ai/img_res/c90e74fb872638060db006cec5d51817.jpg',

  // Services grid
  servicesHeading: 'Electrical Installation Services in Sleaford',
  servicesSubheading: 'From a single new socket to a full commercial electrical fit-out — every electrical installation in Sleaford is carried out to 18th Edition IET Wiring Regulations with full Part P certification.',
  services: [
    { icon: 'ri-plug-fill', title: 'New Socket Installations', desc: 'Additional double sockets, USB outlets and fused spurs installed neatly throughout your Sleaford home or business.' },
    { icon: 'ri-flashlight-fill', title: 'New Circuit Installation', desc: 'Dedicated circuits for cookers, showers, EV chargers or any high-demand appliance across Sleaford properties.' },
    { icon: 'ri-home-fill', title: 'Full House Rewires', desc: 'Complete domestic rewiring in Sleaford from start to finish — certified, documented and done with minimum disruption.' },
    { icon: 'ri-building-fill', title: 'Partial Rewires', desc: 'Part-rewire of specific areas — kitchens, extensions, conversions — fully integrated with your existing installation.' },
    { icon: 'ri-lightbulb-fill', title: 'Lighting Circuits', desc: 'New lighting circuits, switches and fittings — from individual rooms to whole-property lighting designs in Sleaford.' },
    { icon: 'ri-restaurant-fill', title: 'Kitchen Electrical Fit-Outs', desc: 'Full kitchen electrics including cooker circuits, extractor wiring, under-cabinet lighting and socket additions.' },
    { icon: 'ri-building-2-fill', title: 'Extension & Conversion Wiring', desc: 'Complete electrical installation for extensions, loft conversions and garage conversions across Lincolnshire.' },
    { icon: 'ri-tv-fill', title: 'Media & Data Points', desc: 'TV aerial points, HDMI sockets, data outlets and structured cabling for modern connected homes in Sleaford.' },
    { icon: 'ri-switch-fill', title: 'Smart Switch Installation', desc: 'Smart lighting switches and dimmers wired and configured — compatible with Hive, Google, Alexa and more.' },
    { icon: 'ri-global-fill', title: 'Commercial Electrical Fit-Outs', desc: 'Electrical installation services for offices, retail units and commercial premises throughout Sleaford and Lincolnshire.' },
    { icon: 'ri-alarm-warning-fill', title: 'Smoke & CO Alarm Wiring', desc: 'Hard-wired interlinked smoke and carbon monoxide alarms installed to BS 5839 recommendations across Sleaford.' },
    { icon: 'ri-sun-fill', title: 'Solar PV Connection Wiring', desc: 'Inverter and distribution board connection wiring for solar PV systems, ready for panel installation in Lincolnshire.' },
  ],

  // Why Us
  whyUs: [
    {
      icon: 'ri-shield-check-fill',
      title: 'NAPIT Registered',
      desc: 'Every electrical installation in Sleaford is carried out by our NAPIT registered electricians — your guarantee of competent, compliant work.',
    },
    {
      icon: 'ri-file-certificate-fill',
      title: 'Part P Certified',
      desc: 'All notifiable electrical work in Sleaford comes with full Part P Building Regulations certification — essential for insurance and property sales.',
    },
    {
      icon: 'ri-tools-fill',
      title: 'Tidy Finish',
      desc: 'We pride ourselves on a clean, professional finish — neat cables, flush fittings and full reinstatement of any chased surfaces.',
    },
    {
      icon: 'ri-coin-fill',
      title: 'Fixed Quotes',
      desc: 'Written, fixed-price quotes before we start any electrical installation work in Sleaford. No hidden extras, ever.',
    },
  ],

  // Process
  processSteps: [
    {
      number: '01',
      icon: 'ri-phone-fill',
      title: 'Free Quote',
      desc: 'Call or message us for a free no-obligation quote for your electrical installation in Sleaford.',
    },
    {
      number: '02',
      icon: 'ri-map-pin-2-fill',
      title: 'Site Visit',
      desc: 'We visit your Sleaford property to assess requirements and plan the installation correctly.',
    },
    {
      number: '03',
      icon: 'ri-flashlight-fill',
      title: 'Installation',
      desc: 'Our NAPIT qualified electricians carry out all electrical installation work to 18th Edition standards.',
    },
    {
      number: '04',
      icon: 'ri-file-certificate-fill',
      title: 'Certification',
      desc: 'Full Electrical Installation Certificate and Part P sign-off provided on completion — every time.',
    },
  ],

  // Gallery
  gallery: [
    {
      src: 'https://readdy.ai/api/search-image?query=neat%20professional%20electrical%20socket%20installation%20white%20wall%20UK%20modern%20double%20socket%20stainless%20steel%20faceplate%20flush%20mount%20clean%20finish%20no%20people%20Lincolnshire%20domestic%20property%20daylight%20interior%20photography%20warm%20lighting&width=800&height=600&seq=gal-elec-inst-01-v1&orientation=landscape',
      alt: 'New socket installation Sleaford',
      caption: 'New Sockets — Sleaford',
      label: 'Socket Install',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=recessed%20lighting%20circuit%20installation%20UK%20domestic%20ceiling%20white%20plasterboard%20spotlights%20wired%20professional%20finish%20neat%20no%20people%20warm%20interior%20warm%20amber%20light%20kitchen%20living%20room%20Lincolnshire&width=600&height=500&seq=gal-elec-inst-02-v1&orientation=squarish',
      alt: 'New lighting circuit installation Sleaford',
      caption: 'Lighting Circuit — Sleaford',
      label: 'Lighting Circuit',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=professional%20electrical%20wiring%20installation%20inside%20wall%20cavity%20UK%20domestic%20property%20neat%20cable%20routing%20organised%20trunking%20professional%20quality%20no%20people%20close%20up%20white%20wall%20Lincolnshire%20electrical%20work&width=600&height=500&seq=gal-elec-inst-03-v1&orientation=squarish',
      alt: 'New circuit installation Sleaford',
      caption: 'New Circuit Install — Lincolnshire',
      label: 'New Circuit',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=kitchen%20electrical%20fit%20out%20UK%20property%20under%20cabinet%20LED%20strip%20lighting%20socket%20installation%20professional%20finish%20no%20people%20modern%20kitchen%20white%20cabinets%20warm%20ambient%20lighting%20contemporary%20interior&width=600&height=500&seq=gal-elec-inst-04-v1&orientation=squarish',
      alt: 'Kitchen electrical fit-out Sleaford',
      caption: 'Kitchen Fit-Out — Sleaford',
      label: 'Kitchen Electrics',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=loft%20conversion%20electrical%20wiring%20installation%20UK%20property%20neat%20cables%20trunking%20new%20circuits%20spotlights%20fitted%20professional%20finish%20no%20people%20natural%20daylight%20new%20build%20Lincolnshire%20domestic&width=600&height=500&seq=gal-elec-inst-05-v1&orientation=squarish',
      alt: 'Loft conversion electrical installation Sleaford',
      caption: 'Loft Conversion Wiring — Lincolnshire',
      label: 'Conversion Wiring',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=hard%20wired%20smoke%20detector%20alarm%20ceiling%20installation%20UK%20domestic%20property%20white%20ceiling%20neat%20professional%20fitting%20Lincolnshire%20fire%20safety%20electrical%20installation%20no%20people%20close%20up%20detail&width=600&height=500&seq=gal-elec-inst-06-v1&orientation=squarish',
      alt: 'Smoke alarm wiring Sleaford',
      caption: 'Smoke Alarm Wiring — Sleaford',
      label: 'Smoke Alarms',
    },
  ],

  // FAQs
  faqHeading: 'Electrical Installations in Sleaford — FAQs',
  faqSubheading: 'Everything you need to know about getting an electrical installation carried out at your Sleaford property.',
  faqs: [
    {
      q: 'What counts as a notifiable electrical installation in Sleaford?',
      a: 'Under Part P of the Building Regulations, certain electrical installation work must be notified to Building Control. This includes any new circuit in a dwelling, work in kitchens, bathrooms and outdoors, and consumer unit replacements. As NAPIT registered electricians, we self-certify all notifiable work carried out in Sleaford — issuing a Part P Building Regulations Compliance Certificate on completion.',
    },
    {
      q: 'How much does a new circuit installation cost in Sleaford?',
      a: 'The cost of a new circuit installation in Sleaford depends on the type of circuit, cable run lengths, and whether the consumer unit needs upgrading to accommodate it. A straightforward new socket or lighting circuit typically starts from £150–£300. A dedicated high-load circuit (cooker, shower, EV charger) generally costs more due to heavier cabling requirements. We always provide a free written quote before starting any work.',
    },
    {
      q: 'Do you carry out full house rewires in Sleaford?',
      a: 'Yes — full house rewires are one of our core electrical installation services in Sleaford. A rewire involves replacing all the wiring, sockets, switches and light fittings throughout your property. We work methodically room-by-room to minimise disruption, protect your furnishings, and complete the work to the highest standard. All rewires are fully certified under Part P on completion.',
    },
    {
      q: 'What certificate will I receive after an electrical installation in Sleaford?',
      a: 'On completion of any notifiable electrical installation in Sleaford, we issue an Electrical Installation Certificate (EIC) or a Minor Electrical Installation Works Certificate (MEIWC) depending on the scope of work. For Part P notifiable work, you also receive a Building Regulations Compliance Certificate. These documents are essential for insurance purposes and when you sell your property.',
    },
    {
      q: 'Can you add more sockets to my Sleaford home or business?',
      a: 'Absolutely — adding extra sockets is one of the most common electrical installation requests we receive in Sleaford. Whether you need double sockets, USB outlets, floor sockets or outdoor weatherproof sockets, we\'ll install them neatly and certify the work properly. We can typically complete most socket additions in a single visit.',
    },
    {
      q: 'Do you install electrical systems in kitchen or loft extensions in Sleaford?',
      a: 'Yes — we regularly carry out the full electrical installation for new extensions and loft conversions across Sleaford and Lincolnshire. This includes new lighting circuits, socket circuits, heating controls, extraction wiring and any specialist circuits required. All work is co-ordinated with other trades and fully certified under Part P.',
    },
    {
      q: 'Can you wire smoke and CO alarms as part of an electrical installation in Sleaford?',
      a: 'Yes — we highly recommend hard-wired, interlinked smoke and carbon monoxide alarms as part of any electrical installation. Hard-wired alarms are far more reliable than battery-operated models and comply with BS 5839-6 recommendations. We install them as standard on all new builds, rewires and extensions across Sleaford.',
    },
    {
      q: 'How long does a typical electrical installation take in Sleaford?',
      a: 'Timescales vary depending on the scope of work. A simple socket or circuit addition is typically completed in a few hours. A full kitchen electrical fit-out takes a day. A complete house rewire takes 3–5 days depending on the size and access. We always provide a clear timescale at the quote stage so you know exactly what to expect.',
    },
    {
      q: 'Are your electricians qualified to carry out electrical installations in Sleaford?',
      a: 'Yes — all our electricians are fully qualified to Level 3 NVQ in Electrotechnical Services, hold current 18th Edition IET Wiring Regulations certification, and are registered with NAPIT. This means we can self-certify all notifiable electrical installation work in Sleaford without requiring separate Building Control inspection.',
    },
    {
      q: 'Do you carry out commercial electrical installations in Sleaford?',
      a: 'Yes — we carry out commercial electrical installations for offices, retail units, industrial premises, and public buildings throughout Sleaford and Lincolnshire. Commercial work is carried out to BS7671 and all relevant commercial electrical standards. Contact us to discuss your commercial electrical installation requirements.',
    },
  ],

  // Map
  mapHeading: 'Electrical Installations Across Sleaford & Beyond',
  mapSubheading: 'Based in Sleaford, we carry out electrical installations throughout Lincolnshire — from Grantham to Boston, Newark to Stamford.',
  mapEmbedSrc: 'https://maps.google.com/maps?q=Sleaford,Lincolnshire,UK&t=&z=12&ie=UTF8&iwloc=&output=embed',
  mapAreaList: [
    'Sleaford', 'Grantham', 'Boston', 'Spalding', 'Lincoln', 'Newark-on-Trent',
    'Stamford', 'Bourne', 'Heckington', 'Ruskington', 'Ancaster', 'Cranwell',
    'Leadenham', 'Navenby', 'Metheringham', 'Kirkby la Thorpe',
  ],

  // Form
  formUrl: 'https://readdy.ai/api/form/d73qqj4prit363gl0cqg',
};

export default function ElectricalInstallationsPage() {
  return <ServiceTemplate config={config} />;
}
